<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRoute } from 'vue-router'
  import { useCoreStore, useVisitorsStore } from '@/shared/store'
  import { CONTACTS_KEYS } from '@/shared/types/other.types'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import LoaderQC from '@qicgroupdoha/qic-connect-ds/components/LoaderQC'
  import ModalQC from '@qicgroupdoha/qic-connect-ds/components/ModalQC'
  import { useI18n } from 'vue-i18n'
  import { useLinkTarget } from '@/shared/composables/useLinkTarget'

  interface Emits {
    (e: 'close'): void
  }

  const emit = defineEmits<Emits>()

  const { locale } = useI18n()
  const route = useRoute()
  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const gaService = useGoogleAnalytics()
  const isLoading = ref(false)

  const pageName = computed(() => route.params.page || route.name)
  const entryType = visitorsStore.getAnalyticsEntryType

  const emailLink = computed(() => coreStore.getContactValue(CONTACTS_KEYS.EMAIL))
  const waLink = computed(() => coreStore.getContactValue(CONTACTS_KEYS.WALINK_MANDATORY))

  const getContacts = async () => {
    if (!coreStore.contacts.length) {
      try {
        isLoading.value = true
        await coreStore.getContacts()
      } catch {
        console.error('Error loading contacts')
      } finally {
        isLoading.value = false
      }
    }
  }

  onMounted(async () => {
    await getContacts()
  })

  const trackMail = () => gaService.sendEventsByContactUs('email_us', pageName.value, entryType)

  const { getLinkTarget } = useLinkTarget()
  const trackWatsapp = () =>
    gaService.sendEventsByContactUs('whatsApp_us', pageName.value, entryType)
</script>

<template>
  <!-- @vue-ignore -->
  <ModalQC :locale="locale" hide-controls @close="emit('close')">
    <template #content>
      <div class="help-form">
        <!-- @vue-ignore -->
        <div
          class="help-form__title"
          :class="{
            'heading-3': !$qicConnect.isMobile,
            'heading-4': $qicConnect.isMobile,
          }"
        >
          {{ $t('common.get_in_touch') }}
        </div>
        <!-- @vue-ignore -->
        <div
          class="help-form__sub-title"
          :class="{
            'text-regular': !$qicConnect.isMobile,
            'text-small-regular': $qicConnect.isMobile,
          }"
        >
          {{ $t('common.availabilty_hours') }}
        </div>
        <LoaderQC
          v-if="isLoading"
          class="help-form__loader"
          size="extraExtraLarge"
          variant="primary"
        />
        <template v-else>
          <ButtonQC
            wide
            size="medium"
            class="whatsapp-btn"
            icon-before-content="whatsapp"
            :href="waLink"
            :target="getLinkTarget()"
            data-test="need-help-popup-whatsapp-button"
            @click="trackWatsapp"
          >
            {{ $t('qic_need_help_popup_wa_chat') }}
          </ButtonQC>

          <ButtonQC
            wide
            size="medium"
            variant="outline"
            icon-before-content="mail"
            :href="`mailto:${emailLink}`"
            :target="getLinkTarget()"
            data-test="need-help-popup-email-button"
            @click="trackMail"
          >
            {{ emailLink }}
          </ButtonQC>
        </template>
      </div>
    </template>
  </ModalQC>
</template>

<style scoped lang="scss">
  .help-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-5);
  }

  .help-form__title {
    color: var(--colorTxtSrfPrimary);
    text-align: center;
    white-space: pre-line;
  }

  .help-form__sub-title {
    text-align: center;
  }
  .help-form__loader {
    margin: 0 auto;
  }

  .whatsapp-btn.ds-base-button--primary {
    background: var(--colorBgBtnWhatsappDefaultSolid);
    &:hover:not(:disabled) {
      background-color: var(--colorBgBtnWhatsappHoverSolid);
    }
  }
</style>
